/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Card, Text, VStack, Table, Tbody, Th, TableContainer, Spinner } from '@chakra-ui/react';
import { ProductERP } from '@partiu/api/dashboard/types';
import HeaderTable from './atoms/HeaderTable';
import { default_products, getProductTypeFromMetadata } from './atoms/utils';
import { IntegrationPlanRow } from './atoms/IntegrationPlanRow';
import { AgrementProductItem } from './atoms/AgrementPlanItem';
import { Product } from './interfaces';
interface Step1ProductsProps {
  isLoading: boolean;
  products: Product[];
  refetch: () => void;
  filteredProductsERP: ProductERP[];
  sortOrder?: 'asc' | 'desc' | null;
  setSortOrder?: (order: 'asc' | 'desc' | null) => void;
  clientsSortOrder?: 'asc' | 'desc' | null;
  setClientsSortOrder?: (order: 'asc' | 'desc' | null) => void;
  hasListFilter?: boolean;
}

export const Step1Products: React.FC<Step1ProductsProps> = ({
  isLoading,
  refetch,
  filteredProductsERP,
  sortOrder,
  setSortOrder,
  clientsSortOrder,
  setClientsSortOrder,
  products,
  hasListFilter = false,
}) => {
  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (!filteredProductsERP || !Array.isArray(filteredProductsERP) || filteredProductsERP.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
        Nenhum plano encontrado com os critérios de busca
      </Text>
    );
  }

  // Função para ordenar os produtos ERP
  const getSortedProducts = () => {
    const sortedProducts = [...filteredProductsERP];

    // Ordenação por valor (prioridade principal)
    if (sortOrder) {
      sortedProducts.sort((a, b) => {
        const valueA = Number(a.value_plan) || 0;
        const valueB = Number(b.value_plan) || 0;
        const result = sortOrder === 'asc' ? valueA - valueB : valueB - valueA;

        // Se os valores forem iguais, usar ordenação por clientes ativos como critério secundário
        if (result === 0 && clientsSortOrder) {
          const clientsA = Number(a.count_clients_active) || 0;
          const clientsB = Number(b.count_clients_active) || 0;
          return clientsSortOrder === 'asc' ? clientsA - clientsB : clientsB - clientsA;
        }

        return result;
      });
    } else if (clientsSortOrder) {
      // Se não há ordenação por valor, ordenar apenas por clientes ativos
      sortedProducts.sort((a, b) => {
        const clientsA = Number(a.count_clients_active) || 0;
        const clientsB = Number(b.count_clients_active) || 0;
        return clientsSortOrder === 'asc' ? clientsA - clientsB : clientsB - clientsA;
      });
    }

    return sortedProducts;
  };

  const sortedProductsERP = getSortedProducts();

  return (
    <VStack spacing={2} width="100%">
      <Card width="100%" boxShadow="sm">
        <TableContainer maxH="70vh" overflowY="auto" position="relative">
          <Table variant="simple" size="sm">
            <HeaderTable
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              clientsSortOrder={clientsSortOrder}
              setClientsSortOrder={setClientsSortOrder}
              grouping={{
                bancaRevistas: default_products.filter(
                  (product) => product.name === 'Super App' || product.name === 'Revista'
                ).length,
                bibliotecaDigital: default_products.filter(
                  (product) => product.name === 'Literários' || product.name === 'AudioBook' || product.name === 'Profissional' || product.name === 'Educa' || product.name === 'Infantil'
                ).length,
              }}
            >
              {products
                .sort((a, b) => {
                  const order = ['Literários', 'Profissional', 'Educa', 'Infantil', 'AudioBook', 'Super App', 'Revista'];
                  const aType = getProductTypeFromMetadata(a);
                  const bType = getProductTypeFromMetadata(b);
                  const aIndex = order.indexOf(aType);
                  const bIndex = order.indexOf(bType);
 
                  // If aType is found and bType is not, a comes first
                  if (aIndex !== -1 && bIndex === -1) return -1;
                  // If bType is found and aType is not, b comes first
                  if (aIndex === -1 && bIndex !== -1) return 1;
                  // If neither found, keep their order
                  if (aIndex === -1 && bIndex === -1) return 0;
                  // If both found, sort by their index
                  return aIndex - bIndex;
                })
                .map((product) => {
                  const isContracted = true
                  return (
                    <Th
                      key={product.name}
                      fontSize="xs"
                      color="gray.700"
                      fontWeight="bold"
                      textTransform="uppercase"
                      textAlign="center"
                      width="150px"
                      minW="150px"
                      maxW="150px"
                      bg="gray.50"
                      opacity={isContracted ? 1 : 0.5}
                      borderRight="1px solid"
                      borderColor="gray.200"
                    >
                      <AgrementProductItem product={product as any} />
                    </Th>
                  );
                })}
            </HeaderTable>

            <Tbody>
              {sortedProductsERP.map((productERP: ProductERP) => (
                <IntegrationPlanRow
                  key={productERP.id}
                  productERP={productERP}
                  refetch={refetch}
                  products={products as any}
                  hasListFilter={hasListFilter}
                  visiblePlans={hasListFilter ? sortedProductsERP : undefined}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Card>
    </VStack>
  );
};
